import {
  AssetContainerEventType,
  AssetContainerState,
  SPOT_STATUSES_RESERVED,
  SpotStatus,
  YardTaskStatus
} from '@nova/core';

/**
 * Gets the label for a task status
 */
export function getTaskStatusLabel(
  taskStatus?: YardTaskStatus
): 'To Do' | 'In Progress' | 'Completed' | 'Cancelled' {
  switch (taskStatus) {
    case YardTaskStatus.CREATED:
      return 'To Do';
    case YardTaskStatus.IN_PROGRESS:
      return 'In Progress';
    case YardTaskStatus.COMPLETED:
      return 'Completed';
    case YardTaskStatus.CANCELLED:
      return 'Cancelled';
    default:
      return 'To Do';
  }
}

/**
 * Gets the icon for a task status
 */
export function getTaskStatusIcon(taskStatus?: YardTaskStatus): string {
  switch (taskStatus) {
    case YardTaskStatus.CREATED:
      return 'mdi-progress-helper';
    case YardTaskStatus.IN_PROGRESS:
      return 'mdi-progress-clock';
    case YardTaskStatus.COMPLETED:
      return 'mdi-check-circle';
    case YardTaskStatus.CANCELLED:
      return 'mdi-close-circle';
    default:
      return 'mdi-circle-outline';
  }
}

/**
 * Gets the icon/text color for a task status
 */
export function getTaskStatusColor(taskStatus?: YardTaskStatus): string {
  switch (taskStatus) {
    case YardTaskStatus.IN_PROGRESS:
      return 'primary-100';
    case YardTaskStatus.COMPLETED:
      return 'success-100';
    case YardTaskStatus.CANCELLED:
      return 'error-60';
    case YardTaskStatus.CREATED:
    default:
      return 'neutral-80';
  }
}

/**
 * Alias for getTaskStatusColor just so we don't break the useYardTaskPresenter
 */
export { getTaskStatusColor as getTaskStatusIconColor };

/**
 * Gets the background color for a task status
 */
export function getTaskStatusBackgroundColor(taskStatus?: YardTaskStatus): string {
  switch (taskStatus) {
    case YardTaskStatus.IN_PROGRESS:
      return 'primary-20';
    case YardTaskStatus.COMPLETED:
      return 'success-20';
    case YardTaskStatus.CANCELLED:
      return 'error-20';
    case YardTaskStatus.CREATED:
    default:
      return 'neutral-20';
  }
}

/**
 * Returns the Vuetify color for an asset container event type (status).
 * Single source of truth — mirrors useYard.js getAssetContainerStatusColor.
 */
export function getAssetContainerStatusColor(
  status: AssetContainerEventType | string
): string | undefined {
  if (
    status === AssetContainerEventType.Loading ||
    status === AssetContainerEventType.LoadingComplete
  ) {
    return 'warning';
  }
  if (
    status === AssetContainerEventType.Unloading ||
    status === AssetContainerEventType.UnloadingComplete
  ) {
    return 'info';
  }
  if (status === AssetContainerEventType.ReadyForPickup) {
    return 'success';
  }
  if (status === AssetContainerEventType.InTransit) {
    return 'grey';
  }
  if (status === AssetContainerEventType.Inoperable || status === AssetContainerEventType.Missing) {
    return 'error';
  }
  return undefined;
}

/**
 * Returns the MDI icon for an asset container event type (status).
 * Single source of truth — mirrors useYard.js getAssetContainerStatusIcon.
 */
export function getAssetContainerStatusIcon(status: AssetContainerEventType | string): string {
  if (
    status === AssetContainerEventType.LoadingComplete ||
    status === AssetContainerEventType.UnloadingComplete
  ) {
    return 'mdi-check-bold';
  }
  if (status === AssetContainerEventType.Loading) {
    return 'mdi-transfer-down';
  }
  if (status === AssetContainerEventType.Unloading) {
    return 'mdi-transfer-up';
  }
  if (status === AssetContainerEventType.ReadyForPickup) {
    return 'mdi-check-circle';
  }
  if (status === AssetContainerEventType.Departed) {
    return 'mdi-truck-fast-outline';
  }
  if (status === AssetContainerEventType.InTransit) {
    return 'mdi-transit-connection-variant';
  }
  if (status === AssetContainerEventType.Inoperable) {
    return 'mdi-alert-octagon';
  }
  if (status === AssetContainerEventType.Missing) {
    return 'mdi-map-marker-off';
  }
  return 'mdi-progress-clock';
}

/**
 * Returns the MDI icon for a spot occupancy status.
 * Single source of truth for Helios and Circe.
 */
export function getSpotStatusIcon(status: SpotStatus | string): string {
  if (status === SpotStatus.OPEN) {
    return 'mdi-check';
  }
  if (status === SpotStatus.CLOSED) {
    return 'mdi-cancel';
  }
  if (status === SpotStatus.ASSIGNED) {
    return 'mdi-view-column';
  }
  if (status === SpotStatus.IN_TRANSIT) {
    return 'mdi-transit-connection-variant';
  }
  if (SPOT_STATUSES_RESERVED.includes(status as SpotStatus)) {
    return 'mdi-lock-clock';
  }
  return 'mdi-help-circle-outline';
}

/**
 * Returns the Vuetify color for a spot occupancy status.
 * Single source of truth for Helios and Circe.
 */
export function getSpotStatusColor(status: SpotStatus | string): string | undefined {
  if (status === SpotStatus.OPEN) {
    return 'success';
  }
  if (status === SpotStatus.CLOSED) {
    return 'error';
  }
  if (SPOT_STATUSES_RESERVED.includes(status as SpotStatus)) {
    return 'neutral-90';
  }
  if (status === SpotStatus.ASSIGNED) {
    return 'neutral-90';
  }
  if (status === SpotStatus.IN_TRANSIT) {
    return 'warning';
  }
  return undefined;
}

/**
 * Returns the theme colour token for a spot option's background, by occupancy status.
 * Four tones: open, closed, reserved, and everything else.
 */
export function getSpotStatusBackgroundColor(status: SpotStatus | string): string {
  if (status === SpotStatus.OPEN) {
    return 'success-20';
  }
  if (status === SpotStatus.CLOSED) {
    return 'neutral-30';
  }
  if (SPOT_STATUSES_RESERVED.includes(status as SpotStatus)) {
    return 'warning-20';
  }
  return 'neutral-20';
}

/**
 * Returns the MDI icon for an asset container state.
 * Single source of truth — mirrors useYard.js getAssetContainerStateIcon.
 */
export function getAssetContainerStateIcon(state: AssetContainerState | string): string {
  if (state === AssetContainerState.Full) {
    return 'mdi-circle';
  }
  if (state === AssetContainerState.PartiallyLoaded) {
    return 'mdi-circle-half-full';
  }
  return 'mdi-circle-outline';
}
